import { FileText, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { calculateInvoiceTotals, DEFAULT_INVOICE_COVER_PAGE, type InvoiceData } from "@shared/invoice";
import { buildEpcPayment, isValidIban, type InvoicePayment } from "@shared/invoice-payment";

type InvoiceStartPageProps = {
  data: InvoiceData;
  onChange: (patch: Partial<InvoiceData>) => void;
  onSaveDefaults?: () => void;
  savingDefaults?: boolean;
  onApplyPaymentDefaults?: () => void;
  loadingPaymentDefaults?: boolean;
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block space-y-1.5">
        <span className="block text-sm font-medium text-slate-700">{label}</span>
        {children}
      </label>
      {hint && <p className="text-xs leading-relaxed text-slate-500">{hint}</p>}
    </div>
  );
}

export default function InvoiceStartPage({ data, onChange, onSaveDefaults, savingDefaults, onApplyPaymentDefaults, loadingPaymentDefaults }: InvoiceStartPageProps) {
  const coverPage = data.coverPage ?? DEFAULT_INVOICE_COVER_PAGE;
  const updateCoverPage = (patch: Partial<typeof coverPage>) =>
    onChange({ coverPage: { ...coverPage, ...patch } });
  const paymentQr = coverPage.paymentQr;
  const updatePaymentQr = (patch: Partial<InvoicePayment>) => {
    const current = paymentQr ?? { enabled: false, recipient: "", iban: "", bic: "" };
    updateCoverPage({ paymentQr: { ...current, ...patch } });
  };
  let paymentQrError = "";
  if (paymentQr?.enabled) {
    try {
      if (paymentQr.recipient.length > 70) throw new Error("Der Kontoinhaber darf höchstens 70 Zeichen enthalten.");
      if (paymentQr.iban.length > 60) throw new Error("Die IBAN darf höchstens 60 Zeichen enthalten.");
      if (paymentQr.bic.length > 20) throw new Error("Der BIC darf höchstens 20 Zeichen enthalten.");
      buildEpcPayment(paymentQr, calculateInvoiceTotals(data).total, data.invoiceNumber);
    } catch (error) {
      paymentQrError = error instanceof Error ? error.message : "Zahlungsdaten für den QR-Code prüfen.";
    }
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-bauwerk-teal">
            <FileText className="h-4 w-4" />
            Rechnungsdaten
          </CardTitle>
          <p className="text-sm leading-relaxed text-slate-600">
            Übernehmen Sie die Angaben manuell aus Ihrem Buchhaltungsprogramm.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <Field label="Kunde / Firma">
            <Input
              value={data.customerName}
              onChange={(event) => onChange({ customerName: event.target.value })}
              autoComplete="organization"
            />
          </Field>
          <Field label="Projekt">
            <Input
              value={data.projectName}
              onChange={(event) => onChange({ projectName: event.target.value })}
            />
          </Field>
          <Field label="Kundenadresse" hint="Vollständige Rechnungsanschrift">
            <Textarea
              rows={3}
              value={data.customerAddress}
              onChange={(event) => onChange({ customerAddress: event.target.value })}
              autoComplete="street-address"
            />
          </Field>
          <div className="space-y-4">
            <Field label="Kunden-UID (optional)">
              <Input
                value={data.customerUid}
                onChange={(event) => onChange({ customerUid: event.target.value })}
              />
            </Field>
            <Field label="Eigene UID">
              <Input
                value={data.sellerUid}
                onChange={(event) => onChange({ sellerUid: event.target.value })}
              />
            </Field>
          </div>
          <Field
            label="Rechnungsnummer"
            hint="Manuell vergeben; es wird keine Nummer automatisch erzeugt."
          >
            <Input
              value={data.invoiceNumber}
              onChange={(event) => onChange({ invoiceNumber: event.target.value })}
            />
          </Field>
          <Field label="Angebotsreferenz" hint="Referenz zum ursprünglichen Angebot">
            <Input
              value={data.sourceQuoteNumber}
              onChange={(event) => onChange({ sourceQuoteNumber: event.target.value })}
            />
          </Field>
          <Field label="Rechnungsdatum">
            <Input
              type="date"
              value={data.invoiceDate}
              onChange={(event) => onChange({ invoiceDate: event.target.value })}
            />
          </Field>
          <Field label="Fälligkeitsdatum (optional)">
            <Input
              type="date"
              value={data.dueDate}
              onChange={(event) => onChange({ dueDate: event.target.value })}
            />
          </Field>
          <Field label="Leistungszeitraum">
            <Input
              value={data.servicePeriod}
              onChange={(event) => onChange({ servicePeriod: event.target.value })}
            />
          </Field>
          <Field label="Steuersatz">
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                inputMode="decimal"
                value={data.taxRate}
                onChange={(event) =>
                  onChange({
                    taxRate: Math.min(100, Math.max(0, Number(event.target.value) || 0)),
                  })
                }
                className="pr-9 tabular-nums"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                %
              </span>
            </div>
          </Field>
          <Field label="Zahlungsbedingungen">
            <Textarea
              rows={3}
              value={data.paymentTerms}
              onChange={(event) => onChange({ paymentTerms: event.target.value })}
            />
          </Field>
          <Field label="Hinweise">
            <Textarea
              rows={3}
              value={data.notes}
              onChange={(event) => onChange({ notes: event.target.value })}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-bauwerk-teal">Deckblattangaben</CardTitle>
          <p className="text-sm leading-relaxed text-slate-600">
            Diese Angaben erscheinen auf dem Rechnungsdeckblatt.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
            <Field label="Kundennummer">
              <Input
                value={coverPage.customerNumber}
                onChange={(event) => updateCoverPage({ customerNumber: event.target.value })}
              />
            </Field>
            <Field label="Auftragsnummer">
              <Input
                value={coverPage.orderNumber}
                onChange={(event) => updateCoverPage({ orderNumber: event.target.value })}
              />
            </Field>
            <Field label="Bauvorhaben / Referenz">
              <Input
                value={coverPage.projectReference}
                onChange={(event) => updateCoverPage({ projectReference: event.target.value })}
              />
            </Field>
            <Field label="Bearbeiter">
              <Input
                value={coverPage.contactPerson}
                onChange={(event) => updateCoverPage({ contactPerson: event.target.value })}
              />
            </Field>
            <Field label="Eigene DG-Nr.">
              <Input
                value={coverPage.sellerDgNumber}
                onChange={(event) => updateCoverPage({ sellerDgNumber: event.target.value })}
              />
            </Field>
            <Field label="Rechnungstext">
              <Textarea
                rows={3}
                value={coverPage.introText}
                onChange={(event) => updateCoverPage({ introText: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Zusätzliche Firmen- und Bankangaben">
                <Textarea
                  rows={3}
                  value={coverPage.additionalCompanyInfo}
                  onChange={(event) =>
                    updateCoverPage({ additionalCompanyInfo: event.target.value })
                  }
                />
              </Field>
            </div>
          </div>
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs leading-relaxed text-slate-600">
              Eigene UID, eigene DG-Nr., Firmen-/Bankangaben und Zahlungseinstellungen bleiben als Vorgaben für neue Rechnungen in Ihrem Benutzerkonto erhalten. Bestehende Rechnungen werden nicht nachträglich verändert.
            </p>
            {onSaveDefaults && <Button type="button" variant="outline" size="sm" disabled={savingDefaults} onClick={onSaveDefaults}>
              {savingDefaults ? "Vorgaben werden gespeichert…" : "Firmendaten und Zahlungseinstellungen als Vorgabe speichern"}
            </Button>}
          </div>
          <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-bauwerk-teal" />
            <p>Das Standarddeckblatt ist platzsparend angeordnet: Übliche Firmen- und Bankangaben samt Zahlungs-QR-Code passen auf die erste Seite. Lange Texte werden bei Bedarf vollständig auf Folgeseiten fortgesetzt. Leere Deckblattfelder bleiben im PDF leer.</p>
          </div>
          <section aria-labelledby="payment-qr-heading" className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="mb-3 text-xs leading-relaxed text-slate-600">
              Einmal eintragen und die Rechnung speichern: Geänderte Bankdaten und die QR-Einstellung werden automatisch als Vorgabe für neue Rechnungen gespeichert und bleiben jederzeit editierbar. Für bestehende Rechnungen können Sie die gespeicherten Bankdaten hier übernehmen. Betrag und Rechnungsnummer stammen immer aus der jeweiligen Rechnung.
            </p>
            {onApplyPaymentDefaults && <Button type="button" variant="outline" size="sm" className="mb-4"
              disabled={loadingPaymentDefaults} onClick={onApplyPaymentDefaults}>
              {loadingPaymentDefaults ? "Bankdaten werden geladen…" : "Gespeicherte Bankdaten übernehmen"}
            </Button>}
            <div className="flex items-start gap-3">
              <input
                id="payment-qr-enabled"
                type="checkbox"
                checked={paymentQr?.enabled ?? false}
                onChange={(event) => updatePaymentQr({ enabled: event.target.checked })}
                className="mt-1 h-4 w-4 rounded accent-[var(--bauwerk-teal)]"
              />
              <div className="min-w-0 flex-1">
                <label htmlFor="payment-qr-enabled" id="payment-qr-heading" className="cursor-pointer text-sm font-semibold text-slate-800">
                  SEPA-Zahlungs-QR-Code auf dem Deckblatt einfügen
                </label>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Gesamtbetrag und Rechnungsnummer werden automatisch aus dieser Rechnung übernommen{data.invoiceNumber.trim() ? <> (<strong className="font-medium text-slate-700">{data.invoiceNumber}</strong>)</> : ""}. Die Positionsliste wird nicht übertragen. Zum Einlesen ist eine Banking-App mit EPC-/SEPA-QR-Unterstützung erforderlich.
                </p>
              </div>
            </div>
            {paymentQr?.enabled && (
              <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Kontoinhaber" hint="Name des Zahlungsempfängers, wie bei der Bank hinterlegt.">
                    <Input
                      value={paymentQr.recipient}
                      maxLength={70}
                      autoComplete="name"
                      onChange={(event) => updatePaymentQr({ recipient: event.target.value })}
                      aria-invalid={!paymentQr.recipient.trim() || /[\r\n]/.test(paymentQr.recipient) || paymentQr.recipient.length > 70}
                    />
                  </Field>
                  <Field label="IBAN (Volksbank)" hint="Leerzeichen werden beim Erstellen des QR-Codes ignoriert.">
                    <Input
                      value={paymentQr.iban}
                      maxLength={60}
                      autoComplete="off"
                      onChange={(event) => updatePaymentQr({ iban: event.target.value })}
                      aria-invalid={!isValidIban(paymentQr.iban) || paymentQr.iban.length > 60}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="BIC (optional)" hint="Nur erforderlich, wenn Ihre Bank oder Banking-App danach fragt.">
                      <Input
                        value={paymentQr.bic}
                        maxLength={20}
                        autoComplete="off"
                        onChange={(event) => updatePaymentQr({ bic: event.target.value })}
                        aria-invalid={!!paymentQr.bic && (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(paymentQr.bic.replace(/\s/g, "")) || paymentQr.bic.length > 20)}
                      />
                    </Field>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-600">
                  <p>Rechnungsbetrag: <strong className="font-semibold tabular-nums text-slate-800">{new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(calculateInvoiceTotals(data).total)}</strong></p>
                  <p className="mt-1">Rechnungsnummer: <strong className="font-semibold text-slate-800">{data.invoiceNumber.trim() || "wird aus den Rechnungsdaten übernommen"}</strong></p>
                </div>
                {paymentQrError && (
                  <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-relaxed text-red-800">
                    {paymentQrError}
                  </p>
                )}
              </div>
            )}
          </section>
        </CardContent>
      </Card>
    </div>
  );
}
